import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'

export interface StudentRecord {
  student_iid?: string
  iid?: string
  class_roll?: string | number
  student_name_en?: string
  student_name_bn?: string
  father_name_en?: string
  mother_name_en?: string
  group?: string
  mobile?: string
  ssc_roll?: string
  ssc_board?: string
  ssc_year?: string
  reg_no?: string
  subject_code?: string
  optional_subject_code?: string
  session?: string
  gender?: string
  comment?: string
  comment_2?: string
  subject_1?: string
  subject_2?: string
  subject_3?: string
  subject_4?: string
  student_photo_url?: string
  combine_subject_colm?: string
  blood_group?: string
  [key: string]: any
}

interface ColumnDef {
  key: string
  label: string
}

const AVAILABLE_COLUMNS: ColumnDef[] = [
  { key: 'group', label: 'Group' },
  { key: 'class_roll', label: 'Roll' },
  { key: 'student_name_en', label: 'Student Name (EN)' },
  { key: 'student_name_bn', label: 'Student Name (BN)' },
  { key: 'father_name_en', label: 'Father Name' },
  { key: 'mother_name_en', label: 'Mother Name' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'ssc_roll', label: 'SSC Roll' },
  { key: 'ssc_board', label: 'SSC Board' },
  { key: 'ssc_year', label: 'SSC Year' },
  { key: 'reg_no', label: 'Reg No' },
  { key: 'subject_code', label: 'Subject Code' },
  { key: 'optional_subject_code', label: 'Optional Subject Code' },
  { key: 'session', label: 'Session' },
  { key: 'gender', label: 'Gender' },
  { key: 'comment', label: 'Comment' },
  { key: 'comment_2', label: 'Comment 2' },
  { key: 'subject_1', label: 'Subject 1' },
  { key: 'subject_2', label: 'Subject 2' },
  { key: 'subject_3', label: 'Subject 3' },
  { key: 'subject_4', label: 'Subject 4' },
  { key: 'student_photo_url', label: 'Photo' },
  { key: 'combine_subject_colm', label: 'Combine Subject' },
  { key: 'blood_group', label: 'Blood Group' },
  { key: 'student_iid', label: 'Student IID' }
]

const DEFAULT_VISIBLE_COLUMNS = ['group', 'class_roll', 'student_name_en', 'father_name_en', 'mobile', 'student_photo_url']

export default function DataListPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<StudentRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [statusMsg, setStatusMsg] = useState('')

  // Filters & Search
  const [sessionFilter, setSessionFilter] = useState('')
  const [groupFilter, setGroupFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  // Visible Columns
  const [activeColumns, setActiveColumns] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('datalist_2025_visible_columns')
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed) && parsed.length > 0) return parsed
      }
    } catch (e) {
      console.warn('Failed to load column settings', e)
    }
    return DEFAULT_VISIBLE_COLUMNS
  })

  // Settings Modal
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [draftColumns, setDraftColumns] = useState<string[]>(activeColumns)

  // View & Edit Modals
  const [viewingRecord, setViewingRecord] = useState<StudentRecord | null>(null)
  const [editingRecord, setEditingRecord] = useState<StudentRecord | null>(null)
  const [editFormData, setEditFormData] = useState<StudentRecord>({})
  const [savingEdit, setSavingEdit] = useState(false)

  // Photo Hover Preview
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null)

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) {
        navigate('/login', { replace: true })
      } else {
        loadData()
      }
    })
  }, [navigate])

  async function loadData() {
    setLoading(true)
    setStatusMsg('Loading all data...')
    try {
      let allData: StudentRecord[] = []
      let lastId = 0
      const batchSize = 1000
      let pkCol = 'student_iid'

      while (true) {
        let q = supabase.from('database_fgc').select('*')
        if (lastId > 0) q = q.gt(pkCol, lastId)
        q = q.order(pkCol, { ascending: true }).limit(batchSize)

        let res = await q
        if (res.error && pkCol === 'student_iid') {
          pkCol = 'iid'
          q = supabase.from('database_fgc').select('*')
          if (lastId > 0) q = q.gt(pkCol, lastId)
          q = q.order(pkCol, { ascending: true }).limit(batchSize)
          res = await q
        }

        if (res.error) {
          // Fallback to database_fgc or student_database if table doesn't exist
          const fbRes = await supabase.from('database_fgc').select('*').limit(1000)
          if (!fbRes.error && fbRes.data) {
            allData = fbRes.data
          } else {
            const fbRes2 = await supabase.from('student_database').select('*').limit(1000)
            if (!fbRes2.error && fbRes2.data) {
              allData = fbRes2.data
            } else {
              throw res.error
            }
          }
          break
        }

        const batch = res.data ?? []
        if (batch.length === 0) break
        allData = allData.concat(batch)

        const lastRow: any = batch[batch.length - 1]
        const nextId = Number(lastRow.student_iid ?? lastRow.iid ?? 0)
        if (!nextId || nextId === lastId) break
        lastId = nextId
        if (batch.length < batchSize) break
      }

      const sorted = allData.sort((a, b) => (Number(a.class_roll) || 0) - (Number(b.class_roll) || 0))
      setData(sorted)
      setStatusMsg(`Total: ${sorted.length} records loaded`)
    } catch (err: any) {
      console.error('loadData error:', err)
      setStatusMsg(`Data loading error: ${err.message || String(err)}`)
    } finally {
      setLoading(false)
    }
  }

  // Populate filter dropdown options dynamically
  const sessionOptions = useMemo(() => {
    const sSet = new Set<string>()
    data.forEach(r => { if (r.session) sSet.add(String(r.session).trim()) })
    return Array.from(sSet).sort()
  }, [data])

  const groupOptions = useMemo(() => {
    const gSet = new Set<string>()
    data.forEach(r => {
      if (!r.group) return
      if (sessionFilter && String(r.session || '').trim() !== sessionFilter) return
      gSet.add(String(r.group).trim())
    })
    return Array.from(gSet).sort()
  }, [data, sessionFilter])

  // Filtered rows
  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    return data.filter(r => {
      if (sessionFilter && String(r.session || '').trim() !== sessionFilter) return false
      if (groupFilter && String(r.group || '').trim() !== groupFilter) return false
      if (q) {
        const matchRoll = String(r.class_roll || '').toLowerCase().includes(q)
        const matchNameEn = String(r.student_name_en || '').toLowerCase().includes(q)
        const matchNameBn = String(r.student_name_bn || '').toLowerCase().includes(q)
        const matchFather = String(r.father_name_en || '').toLowerCase().includes(q)
        const matchMother = String(r.mother_name_en || '').toLowerCase().includes(q)
        const matchMobile = String(r.mobile || '').toLowerCase().includes(q)
        const matchGroup = String(r.group || '').toLowerCase().includes(q)
        const matchSession = String(r.session || '').toLowerCase().includes(q)
        const matchSscRoll = String(r.ssc_roll || '').toLowerCase().includes(q)
        const matchRegNo = String(r.reg_no || '').toLowerCase().includes(q)
        return matchRoll || matchNameEn || matchNameBn || matchFather || matchMother || matchMobile || matchGroup || matchSession || matchSscRoll || matchRegNo
      }
      return true
    }).sort((a, b) => (Number(a.class_roll) || 0) - (Number(b.class_roll) || 0))
  }, [data, sessionFilter, groupFilter, searchQuery])

  // Save Column Settings
  function saveColumnSettings() {
    if (draftColumns.length === 0) {
      alert('কমপক্ষে একটি কলাম সিলেক্ট করুন!')
      return
    }
    setActiveColumns(draftColumns)
    try {
      localStorage.setItem('datalist_2025_visible_columns', JSON.stringify(draftColumns))
    } catch (e) {
      console.warn('Failed to save settings to localStorage', e)
    }
    setShowSettingsModal(false)
  }

  // Open Edit Modal
  function handleOpenEdit(rec: StudentRecord) {
    setEditingRecord(rec)
    setEditFormData({ ...rec })
  }

  // Save Record Edit to Supabase
  async function handleSaveEdit() {
    if (!editingRecord) return
    const recordId = editingRecord.student_iid || editingRecord.iid
    if (!recordId) {
      alert('Could not determine Record ID')
      return
    }

    setSavingEdit(true)
    try {
      let q = supabase.from('database_fgc').update(editFormData)
      if (editingRecord.student_iid) {
        q = q.eq('student_iid', recordId)
      } else {
        q = q.eq('iid', recordId)
      }

      const { error } = await q
      if (error) throw error

      // Update local state
      setData(prev => prev.map(r => (r.student_iid === recordId || r.iid === recordId) ? { ...r, ...editFormData } : r))
      setEditingRecord(null)
      alert('Record saved successfully!')
    } catch (err: any) {
      alert(`Save error: ${err.message || String(err)}`)
    } finally {
      setSavingEdit(false)
    }
  }

  // Export CSV
  function handleExportCSV() {
    if (filteredRows.length === 0) {
      alert('No data to export')
      return
    }
    const cols = activeColumns
    const headers = ['SL', ...cols.map(c => AVAILABLE_COLUMNS.find(ac => ac.key === c)?.label || c)]
    const rows = filteredRows.map((r, i) => [
      i + 1,
      ...cols.map(c => String(r[c] ?? '').replace(/"/g, '""'))
    ])

    const csvContent = [headers, ...rows].map(row => row.map(val => `"${val}"`).join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `student_list_${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f6f8fa', color: '#24292f', fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}>
      
      {/* HEADER */}
      <header style={{
        background: '#0d1117', color: '#fff', padding: '12px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button 
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
              padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '13px'
            }}
          >← Back to Dashboard</button>
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Student List ALL</h1>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button 
            onClick={handleExportCSV}
            style={{ background: '#10b981', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
          >📥 Export CSV</button>
          <a 
            href="/collegedatabase/login/all_data_field.html" 
            target="_blank" 
            rel="noreferrer"
            style={{
              background: '#ff8a00', color: '#fff', padding: '6px 12px',
              borderRadius: '6px', textDecoration: 'none', fontSize: '13px', fontWeight: 600
            }}
          >All data fields ↗</a>
        </div>
      </header>

      {/* CONTAINER */}
      <div style={{ width: '100%', padding: '18px 16px 40px', boxSizing: 'border-box' }}>
        
        {/* TOOLBAR */}
        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', background: '#fff', padding: '10px 14px', borderRadius: '8px', border: '1px solid #d0d7de', overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap', flexShrink: 0 }}>
            <select 
              value={sessionFilter} 
              onChange={e => setSessionFilter(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #d0d7de', borderRadius: '6px', fontSize: '13px', background: '#fff', minWidth: '130px' }}
            >
              <option value="">All Sessions</option>
              {sessionOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            <select 
              value={groupFilter} 
              onChange={e => setGroupFilter(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #d0d7de', borderRadius: '6px', fontSize: '13px', background: '#fff', minWidth: '130px' }}
            >
              <option value="">All Groups</option>
              {groupOptions.map(g => <option key={g} value={g}>{g}</option>)}
            </select>

            <input 
              type="text" 
              placeholder="Search Roll, Name, Mobile, Group..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #d0d7de', borderRadius: '6px', fontSize: '13px', width: '220px', outline: 'none' }}
            />

            <button 
              onClick={() => { setSessionFilter(''); setGroupFilter(''); setSearchQuery('') }}
              style={{ padding: '7px 12px', background: '#fff', color: '#0366d6', border: '1px solid #0366d6', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}
            >Clear Filters</button>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginLeft: 'auto', whiteSpace: 'nowrap', flexShrink: 0 }}>
            <button 
              onClick={() => { setDraftColumns(activeColumns); setShowSettingsModal(true) }}
              style={{ padding: '7px 12px', background: '#fff', color: '#24292f', border: '1px solid #d0d7de', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
            >⚙️ Column Settings</button>

            <button 
              onClick={loadData}
              style={{ padding: '7px 14px', background: '#0366d6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}
            >Refresh</button>

            {statusMsg && (
              <span style={{ fontSize: '13px', color: '#57606a', marginRight: '12px' }}>
                {statusMsg}
              </span>
            )}
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#10b981', whiteSpace: 'nowrap' }}>
              Total: {filteredRows.length} records loaded
            </span>
          </div>
        </div>

        {/* TABLE */}
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #d0d7de', borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13.5px' }}>
            <thead style={{ background: '#f0f3f6', position: 'sticky', top: 0, zIndex: 5 }}>
              <tr>
                <th style={{ border: '1px solid #d0d7de', padding: '8px 10px', width: '40px', textAlign: 'center' }}>SL</th>
                {activeColumns.map(colKey => {
                  const label = AVAILABLE_COLUMNS.find(c => c.key === colKey)?.label || colKey
                  return (
                    <th key={colKey} style={{ border: '1px solid #d0d7de', padding: '8px 10px', fontWeight: 600, textAlign: 'left' }}>
                      {label}
                    </th>
                  )
                })}
                <th style={{ border: '1px solid #d0d7de', padding: '8px 10px', textAlign: 'center', width: '130px' }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr><td colSpan={activeColumns.length + 2} style={{ padding: '24px', textAlign: 'center' }}>Loading data...</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={activeColumns.length + 2} style={{ padding: '24px', textAlign: 'center', color: '#6a737d' }}>No matching records found</td></tr>
              ) : (
                filteredRows.map((r, idx) => {
                  const isTc = String(r.comment || '').toLowerCase().includes('tc') || String(r.status || '').toLowerCase() === 'tc'
                  const rowBg = isTc ? '#fee2e2' : idx % 2 === 0 ? '#ffffff' : '#fcfcfd'
                  const textColor = isTc ? '#991b1b' : '#24292f'

                  return (
                    <tr key={idx} style={{ background: rowBg, color: textColor, borderBottom: '1px solid #d0d7de' }}>
                      <td style={{ border: '1px solid #d0d7de', padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>{idx + 1}</td>
                      
                      {activeColumns.map(colKey => {
                        const val = r[colKey]
                        if (colKey === 'student_photo_url' && val) {
                          return (
                            <td key={colKey} style={{ border: '1px solid #d0d7de', padding: '6px 8px', textAlign: 'center' }}>
                              <img 
                                src={String(val)} 
                                alt="Student"
                                onMouseEnter={() => setPreviewPhotoUrl(String(val))}
                                onMouseLeave={() => setPreviewPhotoUrl(null)}
                                style={{ width: '40px', height: '48px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #d0d7de', cursor: 'pointer' }}
                              />
                            </td>
                          )
                        }
                        return (
                          <td key={colKey} style={{ border: '1px solid #d0d7de', padding: '8px 10px' }}>
                            {val !== null && val !== undefined ? String(val) : '—'}
                          </td>
                        )
                      })}

                      <td style={{ border: '1px solid #d0d7de', padding: '8px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <button 
                          onClick={() => setViewingRecord(r)}
                          style={{ padding: '5px 9px', background: '#0366d6', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '12px', marginRight: '6px' }}
                        >
                          View
                        </button>
                        <button 
                          onClick={() => handleOpenEdit(r)}
                          style={{ padding: '5px 9px', background: '#ff8a00', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PHOTO PREVIEW HOVER OVERLAY */}
      {previewPhotoUrl && (
        <div style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: '#fff', border: '2px solid #d0d7de', borderRadius: '12px', padding: '12px',
          boxShadow: '0 16px 40px rgba(0,0,0,0.3)', zIndex: 10001, pointerEvents: 'none'
        }}>
          <img src={previewPhotoUrl} alt="Preview" style={{ maxWidth: '400px', maxHeight: '500px', objectFit: 'contain', borderRadius: '8px', display: 'block' }} />
        </div>
      )}

      {/* COLUMN SETTINGS MODAL */}
      {showSettingsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', width: '600px', maxWidth: '90%', borderRadius: '8px', padding: '20px 24px', border: '1px solid #d0d7de', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>⚙️ Setting {'>'} Column Selection</h3>
              <button onClick={() => setShowSettingsModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer' }}>&times;</button>
            </div>
            <p style={{ fontSize: '13px', color: '#666', margin: '0 0 14px 0' }}>টেবিলে যে সকল কলাম দেখতে চান তা সিলেক্ট করুন (লোকালে সেভ থাকবে):</p>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              <button onClick={() => setDraftColumns(AVAILABLE_COLUMNS.map(c => c.key))} style={{ padding: '5px 10px', fontSize: '12px', background: '#fff', color: '#0366d6', border: '1px solid #0366d6', borderRadius: '4px', cursor: 'pointer' }}>Select All</button>
              <button onClick={() => setDraftColumns([])} style={{ padding: '5px 10px', fontSize: '12px', background: '#fff', color: '#0366d6', border: '1px solid #0366d6', borderRadius: '4px', cursor: 'pointer' }}>Deselect All</button>
              <button onClick={() => setDraftColumns(DEFAULT_VISIBLE_COLUMNS)} style={{ padding: '5px 10px', fontSize: '12px', background: '#fff', color: '#0366d6', border: '1px solid #0366d6', borderRadius: '4px', cursor: 'pointer' }}>Reset Default</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '10px', maxHeight: '320px', overflowY: 'auto', padding: '12px', border: '1px solid #d0d7de', borderRadius: '6px', background: '#fafbfc' }}>
              {AVAILABLE_COLUMNS.map(col => (
                <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                  <input 
                    type="checkbox"
                    checked={draftColumns.includes(col.key)}
                    onChange={e => {
                      if (e.target.checked) setDraftColumns(prev => [...prev, col.key])
                      else setDraftColumns(prev => prev.filter(k => k !== col.key))
                    }}
                  />
                  {col.label}
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
              <button onClick={() => setShowSettingsModal(false)} style={{ padding: '8px 16px', background: '#fff', color: '#24292f', border: '1px solid #d0d7de', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={saveColumnSettings} style={{ padding: '8px 18px', background: '#0366d6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }}>Save & Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW RECORD MODAL */}
      {viewingRecord && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', width: '700px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto', borderRadius: '8px', padding: '20px 24px', border: '1px solid #d0d7de', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>View Student Details</h3>
              <button onClick={() => setViewingRecord(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer' }}>&times;</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
              {AVAILABLE_COLUMNS.map(col => (
                <div key={col.key} style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{col.label}</div>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>
                    {viewingRecord[col.key] !== null && viewingRecord[col.key] !== undefined ? String(viewingRecord[col.key]) : '—'}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setViewingRecord(null)} style={{ padding: '8px 18px', background: '#0366d6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT RECORD MODAL */}
      {editingRecord && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', width: '740px', maxWidth: '90%', maxHeight: '85vh', overflowY: 'auto', borderRadius: '8px', padding: '20px 24px', border: '1px solid #d0d7de', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Edit Record</h3>
              <button onClick={() => setEditingRecord(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer' }}>&times;</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px' }}>
              {AVAILABLE_COLUMNS.map(col => (
                <div key={col.key} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#57606a', textTransform: 'uppercase' }}>{col.label}</label>
                  <input 
                    type="text" 
                    value={editFormData[col.key] !== undefined && editFormData[col.key] !== null ? String(editFormData[col.key]) : ''}
                    onChange={e => setEditFormData({ ...editFormData, [col.key]: e.target.value })}
                    style={{ padding: '8px 10px', borderRadius: '4px', border: '1px solid #d0d7de', fontSize: '13px', outline: 'none' }}
                  />
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setEditingRecord(null)} style={{ padding: '8px 16px', background: '#fff', color: '#24292f', border: '1px solid #d0d7de', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={handleSaveEdit} disabled={savingEdit} style={{ padding: '8px 20px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }}>
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
